/**
 * Solid bindings for the gesture recognizer.
 *
 * `pointerHandlers()` returns JSX handlers using Pointer Events + pointer
 * capture (modern; preferred for per-element gestures). Bind them on an element
 * with an appropriate `touch-action` (e.g. `pan-y` for a horizontal swipe) so
 * the browser yields the right axis while the recognizer claims the other.
 */

import type { GestureRecognizer } from "./types";

interface PointerHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerCancel: (e: PointerEvent) => void;
}

/**
 * JSX pointer handlers for a single element. Captures the pointer once the
 * gesture engages (so it keeps tracking outside the element) and releases on
 * up/cancel. Bind on an element with an appropriate `touch-action` (e.g.
 * `touch-pan-y` for a horizontal swipe) so the browser yields the right axis.
 */
export function pointerHandlers(
  recognizer: GestureRecognizer,
  options: { capture?: boolean } = {},
): PointerHandlers {
  const capture = options.capture ?? true;
  let capturedId: number | null = null;

  return {
    onPointerDown(e) {
      if (e.pointerType === "mouse" && e.button !== 0) {
        return;
      }
      recognizer.start(e.clientX, e.clientY, { target: e.target });
    },
    onPointerMove(e) {
      const wasEngaged = recognizer.isEngaged();
      recognizer.move(e.clientX, e.clientY, () => {
        e.preventDefault();
      });
      if (capture && !wasEngaged && recognizer.isEngaged() && e.currentTarget instanceof Element) {
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
          capturedId = e.pointerId;
        } catch {
          /* capture is best-effort */
        }
      }
    },
    onPointerUp(e) {
      recognizer.end(e.clientX, e.clientY);
      if (capturedId !== null && e.currentTarget instanceof Element) {
        try {
          e.currentTarget.releasePointerCapture(capturedId);
        } catch {
          /* element may already be detached */
        }
        capturedId = null;
      }
    },
    onPointerCancel() {
      recognizer.cancel();
      capturedId = null;
    },
  };
}

/**
 * Compose several pointer-handler sets onto a single element so multiple
 * recognizers (e.g. a horizontal chapter swipe and a vertical pull) can share
 * one pointer stream. Each recognizer axis-locks independently, so only the one
 * whose axis matches the drag engages; the others abort harmlessly.
 */
export function mergePointerHandlers(...handlers: PointerHandlers[]): PointerHandlers {
  return {
    onPointerDown(e) {
      for (const h of handlers) {
        h.onPointerDown(e);
      }
    },
    onPointerMove(e) {
      for (const h of handlers) {
        h.onPointerMove(e);
      }
    },
    onPointerUp(e) {
      for (const h of handlers) {
        h.onPointerUp(e);
      }
    },
    onPointerCancel(e) {
      for (const h of handlers) {
        h.onPointerCancel(e);
      }
    },
  };
}
